import {
  Box,
  Button,
  CheckmarkInput,
  Disclaimer,
  Div,
  lightTheme,
  MenulyticsReport,
  MenulyticsShareChannel,
  MenulyticsShareResult,
  ModalPortal,
  P,
  Pill,
  shareMenulyticsReport,
  useBusyAction,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { formatReportMoment } from '../menuHealthFormat';

const CHANNELS: {
  channel: MenulyticsShareChannel;
  label: string;
  description: string;
}[] = [
  {
    channel: 'sms',
    label: 'SMS',
    description: 'A text with the report link, to the owner phone.',
  },
  {
    channel: 'email',
    label: 'Email',
    description: 'View Report and Download PDF buttons.',
  },
  {
    channel: 'chat',
    label: 'Chat',
    description: 'A message from you in the owner support chat, with a push.',
  },
];

const RESULT_STYLE: Record<
  MenulyticsShareResult['status'],
  { label: string; color: string }
> = {
  sent: { label: 'Sent', color: lightTheme.green },
  skipped: { label: 'Skipped', color: lightTheme.darkGray },
  failed: { label: 'Failed', color: lightTheme.primary },
};

const channelLabel = (channel: MenulyticsShareChannel) =>
  CHANNELS.find((row) => row.channel === channel)?.label ?? channel;

type Props = {
  locationId: string;
  /** The report being shared; `null` closes the modal. */
  report: MenulyticsReport | null;
  closeModal: () => void;
  /** Hands back the report with the new `lastSharedAt`. */
  onShared: (report: MenulyticsReport) => void;
};

/**
 * Sends a ready report to the location owner. The channels are independent on
 * the backend, so the answer is one result per channel and is shown as such
 * instead of one success toast.
 */
export const ShareReportModal = ({
  locationId,
  report,
  closeModal,
  onShared,
}: Props) => {
  const [channels, setChannels] = useState<MenulyticsShareChannel[]>([]);
  const [results, setResults] = useState<MenulyticsShareResult[] | null>(null);
  const { busy, run } = useBusyAction();

  const reportId = report?.id;
  useEffect(() => {
    if (reportId) {
      setChannels(CHANNELS.map((row) => row.channel));
      setResults(null);
    }
  }, [reportId]);

  const toggle = (channel: MenulyticsShareChannel, on: boolean) =>
    setChannels((current) =>
      on
        ? [...current.filter((c) => c !== channel), channel]
        : current.filter((c) => c !== channel)
    );

  const send = () =>
    report &&
    run(async () => {
      const { data } = await shareMenulyticsReport(locationId, report.id, {
        // In the order the modal lists them, whatever order they were ticked.
        channels: CHANNELS.map((row) => row.channel).filter((c) =>
          channels.includes(c)
        ),
      });
      setResults(data.data.results);
      onShared(data.data.report);
    });

  return (
    <ModalPortal
      isOpen={!!report}
      closeModal={closeModal}
      logo={false}
      header="Share with owner"
      zIndex={140}
      footer={
        results ? (
          <Button $w="100%" $wSM="100%" $selected onClick={closeModal}>
            Done
          </Button>
        ) : (
          <>
            {/* Right above Send, where it is read before sending again. */}
            {report?.lastSharedAt && (
              <Disclaimer $m="0 0 12px" $center $color={lightTheme.darkGray}>
                Last sent on {formatReportMoment(report.lastSharedAt)}
              </Disclaimer>
            )}
            <Button
              $w="100%"
              $wSM="100%"
              $selected
              $loading={busy}
              disabled={busy || channels.length === 0}
              onClick={send}
            >
              Send
            </Button>
          </>
        )
      }
    >
      {results ? (
        <Div $flex $column $gap="8px">
          {results.map((result) => {
            const style = RESULT_STYLE[result.status];
            return (
              <Box key={result.channel} $flex $between $alignCenter $gap="16px">
                <Div $minWidth="0">
                  <P $m="0" $bold $left>
                    {channelLabel(result.channel)}
                  </P>
                  {result.message && (
                    <Disclaimer $m="2px 0 0" $left $color={lightTheme.darkGray}>
                      {result.message}
                    </Disclaimer>
                  )}
                </Div>
                <Div $flexNone>
                  <Pill $background={style.color}>
                    <Disclaimer $m="0" $color={lightTheme.white} $uppercase>
                      {style.label}
                    </Disclaimer>
                  </Pill>
                </Div>
              </Box>
            );
          })}
        </Div>
      ) : (
        <>
          <P $m="0 0 16px" $left $color={lightTheme.darkGray}>
            The report goes to the location owner.
          </P>
          <Div $flex $column $gap="8px">
            {CHANNELS.map((row) => (
              // A label, so a click anywhere on the box ticks it.
              <Box
                key={row.channel}
                as="label"
                $flex
                $alignCenter
                $gap="12px"
                $pointer
              >
                <CheckmarkInput
                  checked={channels.includes(row.channel)}
                  onChange={(on) => toggle(row.channel, on)}
                />
                <Div $minWidth="0">
                  <P $m="0" $bold $left>
                    {row.label}
                  </P>
                  <Disclaimer $m="2px 0 0" $left $color={lightTheme.darkGray}>
                    {row.description}
                  </Disclaimer>
                </Div>
              </Box>
            ))}
          </Div>
        </>
      )}
    </ModalPortal>
  );
};
